<template>
	<v-card class="ma-2 full-height-card">
		<div class="d-flex flex-row align-center justify-space-between pa-4" style="width: 100%">
			<div class="d-flex flex-row align-center">
				<v-text-field
					class="mr-2"
					v-model="search"
					:label="$t('search')"
					append-icon="mdi-magnify"
					single-line
					hide-details
					outlined
					dense
					clearable />
				<v-select
					v-model="selectedType"
					:label="$t('document-type')"
					:items="typeOptions"
					item-text="text"
					item-value="value"
					outlined
					dense
					hide-details
					:menu-props="{ maxHeight: 200, offsetY: true }" />
			</div>
			<v-icon
				color="primary"
				size="25"
				@click="synchronizeAddDrawers('releaseNotes')"
				v-if="roleName === 'SYSTEM_ADMIN' || roleName === 'SYSTEM_USER'">
				mdi-plus-circle
			</v-icon>
		</div>
		<v-card-text style="height: calc(100% - 72px)" class="overflow-y-auto pt-0">
			<v-data-table
				@click:row="openViewDialog"
				:items="filteredDocuments"
				:headers="headers"
				:items-per-page="-1"
				:loading="loading"
				:search="search"
				style="overflow-y: auto"
				fixed-header
				:height="pageHeight(124)"
				:item-class="itemClass"
				dense
				sort-by="uploadedAt"
				sort-desc
				class="data-table-outline">
				<template #[`item.fileName`]="{ item }">
					<v-icon color="black" size="21">
						{{ getFileIcon(item.fileName) }}
					</v-icon>
					{{ item.fileName }}
				</template>
				<template #[`item.uploadedAt`]="{ item }">
					{{ formatTimestamp(item.uploadedAt) }}
				</template>
				<template #[`item.dedicatedToOrganizationId`]="{ item }">
					{{ getOrganizationName(item.dedicatedToOrganizationId) }}
				</template>
				<template #[`item.actions`]="{ item }">
					<ActionButton
						v-if="roleName === 'SYSTEM_ADMIN' || roleName === 'SYSTEM_USER'"
						icon="mdi-delete"
						tooltip="delete"
						:maxSize="25"
						:minSize="25"
						:busySpinnerColor="'primary'"
						@clicked="
							deleteId = item.id;
							showDeleteDialog = true;
						"
						:busy="deletingIds.includes(item.id)" />
					<ActionButton
						icon="mdi-cloud-download"
						tooltip="download-file"
						:busySpinnerColor="'primary'"
						:maxSize="25"
						:minSize="25"
						@clicked="downloadFile(item)"
						:busy="downloadingIds.includes(item.id)" />
					<ActionButton
						icon="mdi-open-in-new"
						tooltip="open-in-new-tab"
						:busySpinnerColor="'primary'"
						:disabled="excludedFileExtensions.includes(item.fileExtension)"
						disabledTooltip="unsupported-file-type"
						:maxSize="25"
						:minSize="25"
						@clicked="openFileInNewTab(item)"
						:busy="openingIds.includes(item.id)" />
				</template>
				<template #[`footer.prepend`]>
					<TransmissionJobsDialog
						:access="roleName === 'SYSTEM_ADMIN' || roleName === 'SYSTEM_USER' ? 'both' : 'download'"
						:showDialog="showUploadDialog"
						:jobs="jobs"
						@closeDialog="showUploadDialog = false"
						@delete-job="(id) => deleteJob(id)" />
				</template>
			</v-data-table>
		</v-card-text>
		<ReleaseNoteDialog
			:dialog.sync="showViewDialog"
			v-if="showViewDialog && selectedDocument"
			:selectedDocument="selectedDocument"
			@closeDialog="showViewDialog = false" />
		<DeleteDialog
			:dialog.sync="showDeleteDialog"
			@confirm="
				showDeleteDialog = false;
				deletingIds.push(deleteId);
				deleteDocument(deleteId);
				deleteId = -1;
			" />
	</v-card>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { formatTimestamp } from '../../../utils/utilities';
import ActionButton from './ActionButton.vue';
import ReleaseNoteDialog from '../../dialogs/ReleaseNoteDialog.vue';
import DeleteDialog from '@/views/dialogs/DeleteDialog.vue';
import TransmissionJobsDialog from '@/views/dialogs/TransmissionJobsDialog.vue';

export default {
	name: 'releaseNotesAndManuals',
	components: { ActionButton, ReleaseNoteDialog, DeleteDialog, TransmissionJobsDialog },
	data () {
		return {
			deletingIds: [],
			downloadingIds: [],
			openingIds: [],
			showDeleteDialog: false,
			deleteId: null,
			selectedDocument: null,
			showViewDialog: false,
			loading: true,
			selectedType: null,
			search: '',
			excludedFileExtensions: [
				'doc',
				'docx',
				'ppt',
				'pptx',
				'xlsx',
				'xls',
				'7z',
				'rar',
				'tar',
				'zip',
				'ods',
				'odp',
				'odt',
			],
			showUploadDialog: false,
		};
	},
	computed: {
		...mapState({
			releaseNote: (state) => state.releaseNotes.releaseNote,
			releaseNotes: (state) => state.releaseNotes.releaseNotes,
			medicalInstitutions: (state) => state.medicalInstitutions.medicalInstitutions,
			organizationId: (state) => state.authentication.organizationId,
			jobs: (state) => state.releaseNotes.jobs,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
			roleName: 'authentication/getRole',
		}),
		headers () {
			const headers = [
				{ text: this.$t('type'), value: 'typeOfDocument' },
				{ text: this.$t('file-name'), value: 'fileName' },
				{ text: this.$t('descriptions'), value: 'documentDescription', cellClass: 'truncate-text-25' },
				{ text: this.$t('organization'), value: 'dedicatedToOrganizationId' },
				{ text: this.$t('date'), value: 'uploadedAt' },
				{ text: this.$t('actions'), value: 'actions', align: 'right', sortable: false },
			];
			return headers;
		},
		filteredDocuments () {
			let documents = !this.selectedType
				? this.releaseNotes
				: this.releaseNotes.filter((note) => note.typeOfDocument === this.selectedType);
			if (this.roleName !== 'SYSTEM_ADMIN' && this.roleName !== 'SYSTEM_USER') {
				documents = documents.filter(
					(doc) =>
						doc.fileExtension === 'pdf' &&
						(doc.dedicatedToOrganizationId === null || doc.dedicatedToOrganizationId === this.organizationId),
				);
			}
			documents = documents.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
			return documents.map((item, index) => ({
				...item,
				index: index,
			}));
		},
		typeOptions () {
			return [
				{ text: this.$t('all'), value: null },
				{ text: this.$t('user-guide'), value: 'User Guide' },
				{ text: this.$t('release-note'), value: 'Release Note' },
			];
		},
	},
	async mounted () {
		await this.getMedicalInstitutions();
		await this.getAllReleaseNotes();
		this.loading = false;
	},
	methods: {
		...mapActions({
			getAllReleaseNotes: 'releaseNotes/getAllReleaseNotes',
			getMedicalInstitutions: 'medicalInstitutions/getMedicalInstitutions',
			deleteReleaseNote: 'releaseNotes/deleteReleaseNote',
			getReleaseNote: 'releaseNotes/getReleaseNoteById',
		}),
		...mapMutations({
			deleteJob: 'releaseNotes/DELETE_JOB',
		}),
		formatTimestamp,
		async deleteDocument (id) {
			await this.deleteReleaseNote(id);
			this.deletingIds = this.deletingIds.filter((deletingId) => deletingId !== id);
		},
		async downloadFile (item) {
			this.downloadingIds.push(item.id);
			try {
				await this.getReleaseNote(item);
				const url = window.URL.createObjectURL(new Blob([this.releaseNote]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', item.fileName);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
			} finally {
				this.downloadingIds = this.downloadingIds.filter((downloadingId) => downloadingId !== item.id);
			}
		},
		getFileIcon (fileName) {
			const nameArray = fileName.toString().split('.');
			const fileType = nameArray[nameArray.length - 1];

			const fileTypeMap = {
				pdf: 'mdi-file-powerpoint',
				jpeg: 'mdi-file-image',
				jpg: 'mdi-file-image',
				png: 'mdi-file-image',
				txt: 'mdi-file-document',
				doc: 'mdi-file-document',
				docx: 'mdi-file-document',
				xls: 'mdi-file-table',
				xlsx: 'mdi-file-table',
				ppt: 'mdi-file-chart',
				pptx: 'mdi-file-chart',
				csv: 'mdi-file-delimited',
				zip: 'mdi-zip-box',
				'7z': 'mdi-zip-box',
				rar: 'mdi-zip-box',

				// Add more mappings as needed
			};
			return fileTypeMap[fileType] || 'mdi-file';
		},
		getOrganizationName (id) {
			if (!id) return this.$t('all');
			const organization = this.medicalInstitutions.find((org) => org.id === id);
			return organization ? organization.institutionName : '';
		},
		async openFileInNewTab (item) {
			try {
				this.openingIds.push(item.id);
				await this.getReleaseNote(item);
				const url = window.URL.createObjectURL(this.releaseNote);
				const link = document.createElement('a');
				link.href = url;
				window.open(link, '_blank');
			} finally {
				this.openingIds = this.openingIds.filter((id) => id !== item.id);
			}
		},
		openViewDialog (item) {
			this.selectedDocument = item;
			this.showViewDialog = true;
		},
		synchronizeAddDrawers (drawer) {
			const parameters = {
				detailsDrawer: true,
				page: drawer,
			};
			this.$store.commit('SET_DETAILS_DRAWER', parameters);
		},
		itemClass (item) {
			return item.index % 2 ? 'odd-row' : '';
		},
	},
};
</script>
<style>
.full-height-card {
	height: calc(100vh - 77px) !important;
}
.truncate-text-25 {
	white-space: nowrap;
	text-overflow: ellipsis;
	overflow: hidden;
	max-width: 25vw !important;
}
</style>
